import type { DocumentsChecklistDoc } from "../types/checklist.types";
import type { DataDocs } from "../types/tables-data";
import type { QualityIssue, QualityIssueDesc } from "../types/quality-issue";
import axiosInstance from "./axiosConfig";
import { auth } from "./firebaseApi";
import { extractFilenameFromContentDisposition } from "./utils/file";

export type UpdateDocumentPayload = {
  exclude_from_exhibit_list: boolean;
  remove_exhibit_number?: boolean;
};

export type UpdateCategoryExhibitNumberPayload = {
  super_class: string;
  category: string;
  remove_exhibit_number: boolean;
};

export type UpdateCategoryExhibitNumberResponse = {
  super_class: string;
  category: string;
  remove_exhibit_number: boolean;
  updated_document_ids: string[];
};

export type ExtractedField = {
  variableName: string;
  fieldLabel: string;
  fieldType: "text" | "number" | "date" | "object" | "array";
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  value: string | number | boolean | null | ExtractedField[];
};

export type DocumentStatus = {
  status: string;
  taskType?: string | null;
  errorMsg?: string | null;
};

export type Document = {
  id: string;
  exclude_from_exhibit_list: boolean;
  remove_exhibit_number?: boolean;
  autoTitle?: string;
  documentTitle?: string;
  // Quality warnings
  quality_issues?: Array<QualityIssue | QualityIssueDesc>;
  batch_quality_warnings?: QualityIssueDesc[];
  // legacy naming (backend may still send this)
  batch_quality_issues?: QualityIssueDesc[];
  updated_at?: string | null;
  title_updated_at?: string | null;
  extracted_fields_updated_at?: string | null;
  filePath?: string;
  uploaded_file_path?: string;
  pdf_file_path?: string;
  autoOCRText?: string;
  extracted_fields?: ExtractedField[];
  extracted_argument?: Record<string, unknown>;
  uploadDate?: string;
  uploadBy?: string;
  isRequireForForms?: boolean;
  isRequireForDraft?: boolean;
  docNames?: string;
  docUrls?: string;
  description?: string;
  text?: string;
  isNew?: boolean;
  isDeleted?: boolean;
  super_class?: string;
  type?: string | null;
  customType?: string | null;
  criterion?: string | null;
  exhibit?: string;
  summaryUpdatedAt?: string;
  classifier_updated_at?: string;
  summary?: string | null;
  timeline_updated_at?: string | null;
  timeline?: string | null;
  status?: DocumentStatus | null;
  source_type?: string;
  source_topic_id?: string;
  source_attachment_document_id?: string;
  source_url?: string;
};

export type UpdateDocumentMetadataPayload = {
  autoTitle?: string | null;
  extracted_fields?: ExtractedField[];
  summary?: string | null;
  timeline?: string | null;
};

export type PatchDocumentsRootPayload = {
  status?: DocumentStatus;
  lastUploadBy?: string;
  lastUploadByUid?: string;
  last_updated_by?: string;
};

export type PatchDocumentPayload = {
  isNew?: boolean;
  extracted_argument?: Record<string, unknown> | null;
  last_updated_by?: string;
};

/**
 * Fields the bulk-patch endpoint accepts. The backend enforces the same
 * allowlist, so anything outside this type is a 400 rather than a silent
 * partial write.
 */
export type BulkPatchDocumentFields = {
  super_class?: string | null;
  criterion?: string | null;
  type?: string | null;
  customType?: string | null;
  reclassify?: boolean;
  exclude_from_exhibit_list?: boolean;
  autoTitle?: string;
  autoTitleStatus?: string;
  documentTitle?: string;
  description?: string;
  timeline?: string;
  timelineHtml?: string;
  summary?: string;
  extracted_fields?: unknown;
  sub_documents?: unknown[];
  isNew?: boolean;
  isDeleted?: boolean;
};

export type BulkPatchDocumentItem = {
  document_id: string;
  /** Defaults to evidence_docs; "docs" addresses the legacy standard docs. */
  collection?: "evidence_docs" | "docs";
  /** Set for a bundled child under evidence_docs/{parent}/docs/{child}. */
  parent_document_id?: string;
  fields: BulkPatchDocumentFields;
};

export type BulkPatchDocumentsResponse = {
  patched: number;
};

export type GenerateDocumentTitlePayload = {
  skip_expert_documents?: boolean;
};

export type AddCaseResearchSourceDocumentPayload = {
  topic_id: string;
  url: string;
  attachment_document_id?: string;
  provider?: "cloudflare" | "pdfbolt" | "pdfmyurl";
};

export type RecategorizeDocumentPayload = {
  super_class: string;
  criterion: string;
  type: string;
  customType?: string | null;
};

export type DocumentFileResponse = {
  filePath: string;
  data: string; // base64
};

export const DocumentsAPI = {
  async updateDocument(
    individual_id: string,
    document_id: string,
    updates: UpdateDocumentPayload
  ): Promise<Document> {
    const response = await axiosInstance.put<Document>(
      `/case/${individual_id}/document/${document_id}`,
      updates
    );
    return response.data;
  },

  async setExcludeFromExhibitList(
    individual_id: string,
    document_id: string,
    exclude_from_exhibit_list: boolean
  ): Promise<Document> {
    const response = await axiosInstance.put<Document>(
      `documents/${individual_id}/document/${document_id}/exclude-from-exhibit-list`,
      { exclude_from_exhibit_list }
    );
    return response.data;
  },

  async setRemoveExhibitNumber(
    individual_id: string,
    document_id: string,
    remove_exhibit_number: boolean
  ): Promise<Document> {
    const response = await axiosInstance.put<Document>(
      `documents/${individual_id}/document/${document_id}/remove-exhibit-number`,
      { remove_exhibit_number }
    );
    return response.data;
  },

  async setRemoveCategoryExhibitNumber(
    individual_id: string,
    payload: UpdateCategoryExhibitNumberPayload
  ): Promise<UpdateCategoryExhibitNumberResponse> {
    const response =
      await axiosInstance.put<UpdateCategoryExhibitNumberResponse>(
        `documents/${individual_id}/category/remove-exhibit-number`,
        payload
      );
    return response.data;
  },

  async patchDocumentsRoot(
    individual_id: string,
    payload: PatchDocumentsRootPayload
  ): Promise<void> {
    await axiosInstance.patch(`/documents/${individual_id}`, payload);
  },

  async patchDocument(
    individual_id: string,
    document_id: string,
    payload: PatchDocumentPayload
  ): Promise<Document> {
    const response = await axiosInstance.patch<Document>(
      `/documents/${individual_id}/document/${document_id}`,
      payload
    );
    return response.data;
  },

  /**
   * Patch fields on one or more document records.
   *
   * Replaces direct Firestore writes from the document table; the backend
   * owns the field allowlist and ownership stamp, so use this even for a
   * single document.
   */
  async bulkPatchDocuments(
    individual_id: string,
    documents: BulkPatchDocumentItem[]
  ): Promise<BulkPatchDocumentsResponse> {
    const response = await axiosInstance.post<BulkPatchDocumentsResponse>(
      `/documents/${individual_id}/documents/bulk-patch`,
      { documents }
    );
    return response.data;
  },

  async bulkDeleteDocuments(
    individual_id: string,
    document_ids: string[]
  ): Promise<number> {
    const response = await axiosInstance.post<{ deleted: number }>(
      `/documents/${individual_id}/bulk-delete`,
      { document_ids }
    );
    return response.data.deleted;
  },

  async hardDeleteDocument(
    individual_id: string,
    document_id: string,
    collection: "evidence_docs" | "docs" = "evidence_docs"
  ): Promise<void> {
    await axiosInstance.post(
      `/documents/${individual_id}/document/${document_id}/hard-delete`,
      { collection }
    );
  },

  async getDocumentThumbnail(
    individual_id: string,
    document_id: string
  ): Promise<string> {
    const response = await axiosInstance.get<{ thumbnail: string }>(
      `/documents/${individual_id}/document/${document_id}/thumbnail`
    );
    return response.data.thumbnail;
  },

  async getDocumentsChecklist(
    visaType: string
  ): Promise<DocumentsChecklistDoc> {
    const response = await axiosInstance.get<DocumentsChecklistDoc>(
      `/documents/checklist/${visaType}`
    );
    return response.data;
  },

  async getDocuments(individual_id: string): Promise<DataDocs[]> {
    const response = await axiosInstance.get<DataDocs[]>(
      `/documents?individual_id=${individual_id}`
    );
    return response.data;
  },

  async getDocumentPdf(
    individualId: string,
    documentId: string
  ): Promise<Blob> {
    const response = await axiosInstance.get<Blob>(
      `/documents/${individualId}/document/${documentId}/pdf`,
      {
        responseType: "blob"
      }
    );
    return response.data;
  }
};

/**
 * Documents review endpoints (Flask `/api/documents...`).
 * This is intentionally separate from the legacy DocumentsAPI above.
 */
export const DocumentsReviewAPI = {
  async getDocuments(
    individualId: string,
    documentId?: string
  ): Promise<Document[] | Document> {
    const response = await axiosInstance.get<Document[] | Document>(
      "/documents",
      {
        params: {
          individual_id: individualId,
          ...(documentId ? { document_id: documentId } : {})
        }
      }
    );
    return response.data;
  },

  async queueCaseKnowledgeRefresh(individualId: string): Promise<void> {
    await axiosInstance.post("/case-knowledge/refresh/queue", {
      individual_id: individualId
    });
  },

  async rerunExtraction(
    individualId: string,
    documentId: string
  ): Promise<void> {
    await axiosInstance.post(
      `/documents/${individualId}/document/${documentId}/rerun-extraction`
    );
  },

  async getBundledDocuments(
    individualId: string,
    parentDocumentId: string
  ): Promise<Record<string, unknown>[]> {
    const response = await axiosInstance.get<Record<string, unknown>[]>(
      `/documents/${individualId}/document/${parentDocumentId}/bundled`
    );
    return response.data;
  },

  async getDocumentFile(
    individualId: string,
    documentId: string
  ): Promise<DocumentFileResponse> {
    const response = await axiosInstance.get<DocumentFileResponse>(
      `/documents/${individualId}/document/${documentId}/file`
    );
    return response.data;
  },

  async updateDocumentMetadata(
    individualId: string,
    documentId: string,
    updates: UpdateDocumentMetadataPayload
  ): Promise<Document> {
    const response = await axiosInstance.put<Document>(
      `/documents/${individualId}/document/${documentId}/metadata`,
      updates
    );
    return response.data;
  },

  async generateDocumentTitle(
    individualId: string,
    documentId: string,
    payload: GenerateDocumentTitlePayload = {}
  ): Promise<Document> {
    const response = await axiosInstance.post<Document>(
      `/documents/${individualId}/document/${documentId}/generate-title`,
      payload
    );
    return response.data;
  },

  async recategorizeDocument(
    individualId: string,
    documentId: string,
    updates: RecategorizeDocumentPayload
  ): Promise<Document> {
    const response = await axiosInstance.put<Document>(
      `/documents/${individualId}/document/${documentId}/recategorize`,
      updates
    );
    return response.data;
  },

  async deleteDocument(
    individualId: string,
    documentId: string
  ): Promise<Document> {
    const response = await axiosInstance.delete<Document>(
      `/documents/${individualId}/document/${documentId}`
    );
    return response.data;
  },

  async downloadDocumentsZip(
    individualId: string,
    documentIds: string[]
  ): Promise<{ blob: Blob; fileName: string }> {
    const response = await axiosInstance.post(
      `/documents/${individualId}/download`,
      { document_ids: documentIds },
      { responseType: "arraybuffer" }
    );

    const contentDisposition: string | undefined =
      response.headers["content-disposition"];
    const contentType: string =
      response.headers["content-type"] || "application/zip";
    const fileName =
      extractFilenameFromContentDisposition(contentDisposition) ||
      `documents_${individualId}.zip`;

    const blob = new Blob([response.data as ArrayBuffer], {
      type: contentType
    });
    return { blob, fileName };
  },

  async addCaseResearchSourceDocument(
    individualId: string,
    payload: AddCaseResearchSourceDocumentPayload
  ): Promise<Document> {
    const response = await axiosInstance.post<Document>(
      `/documents/${individualId}/case-research/source`,
      payload
    );
    return response.data;
  }
};
